import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Empty, Spin, Tag } from "antd";
import {
  ArrowRightOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileDoneOutlined,
  RiseOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import apiClient from "../actions/api";
import "../style/dashboard-overview.scss";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const statusName = (status) =>
  ({
    0: "Waiting",
    1: "Recruiter phone",
    2: "Recruiter video",
    3: "Technical",
    4: "Behavioural",
    5: "Final",
    6: "Failed",
    7: "HR call",
  })[status] || "In progress";

const emptyCounts = {
  total: 0,
  progress: 0,
  interviews: 0,
  failed: 0,
  waiting: 0,
};

const DashboardOverview = ({ title }) => {
  document.title = title;
  const user = useSelector((state) => state.auth.user);
  const timezone = useSelector((state) => state.timezoneMode.timezoneMode);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({
    counts: emptyCounts,
    recentApplications: [],
    upcomingInterviews: [],
    performance: [],
  });

  const loadDashboard = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    try {
      const response = await apiClient.get("/dashboard/overview", {
        params: {
          startDate: dayjs()
            .tz(timezone)
            .subtract(6, "day")
            .startOf("day")
            .toISOString(),
          endDate: dayjs().tz(timezone).endOf("day").toISOString(),
          timezone,
        },
      });
      setDashboard(response.data);
    } catch {
      setDashboard({
        counts: emptyCounts,
        recentApplications: [],
        upcomingInterviews: [],
        performance: [],
      });
    } finally {
      setLoading(false);
    }
  }, [timezone, user?._id]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const interviews = dashboard.upcomingInterviews || [];
  const recent = dashboard.recentApplications || [];
  const counts = dashboard.counts || emptyCounts;
  const performance = useMemo(
    () =>
      (dashboard.performance || []).map((day) => ({
        ...day,
        label: dayjs(day.date).format("ddd"),
      })),
    [dashboard.performance],
  );
  const performanceMax = Math.max(
    1,
    ...performance.flatMap((day) => [
      day.applications,
      day.interviews,
      day.failed,
      day.waiting,
    ]),
  );

  return (
    <div className="overview-page">
      <header className="overview-heading">
        <div>
          <h1>Dashboard</h1>
          <p>
            Track applications, interviews, and progress from one clear view.
          </p>
        </div>
        <Link to="/applications">
          <Button type="primary">
            View applications <ArrowRightOutlined />
          </Button>
        </Link>
      </header>
      <Spin spinning={loading}>
        <section className="overview-stat-grid">
          {[
            [<FileDoneOutlined />, "Applications", counts.total, "blue"],
            [<RiseOutlined />, "In progress", counts.progress, "cyan"],
            [<CalendarOutlined />, "Interviews", counts.interviews, "purple"],
            [<CloseCircleOutlined />, "Failed stage", counts.failed, "red"],
            [<StarOutlined />, "Waiting", counts.waiting, "gold"],
          ].map(([icon, label, value, color]) => (
            <article key={label}>
              <span className={color}>{icon}</span>
              <div>
                <small>{label}</small>
                <strong>{value}</strong>
              </div>
            </article>
          ))}
        </section>

        <section className="overview-main-grid">
          <article className="overview-panel overview-recent">
            <div className="overview-panel-head">
              <div>
                <h2>Recent applications</h2>
                <p>Your latest application activity</p>
              </div>
              <Link to="/applications">
                View all <ArrowRightOutlined />
              </Link>
            </div>
            {recent.length ? (
              <div className="overview-table">
                <div className="overview-table-row overview-table-header">
                  <span>Role</span>
                  <span>Company</span>
                  <span>Status</span>
                  <span>Applied</span>
                </div>
                {recent.map((item) => (
                  <div className="overview-table-row" key={item._id}>
                    <strong>{item.role_title || "Untitled role"}</strong>
                    <span>{item.company_name || "—"}</span>
                    <span>
                      <Tag
                        color={
                          Number(item.status) === 6
                            ? "red"
                            : Number(item.status) === 0
                              ? "gold"
                              : "cyan"
                        }
                      >
                        {statusName(item.status)}
                      </Tag>
                    </span>
                    <span>
                      {dayjs(item.date).fromNow?.() ||
                        dayjs(item.date).format("DD MMM")}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No recent applications"
              />
            )}
          </article>

          <article className="overview-panel overview-interviews">
            <div className="overview-panel-head">
              <div>
                <h2>Upcoming interviews</h2>
                <p>Your scheduled conversations</p>
              </div>
              <Link to="/calendar">Calendar</Link>
            </div>
            {interviews.length ? (
              <div className="overview-interview-list">
                {interviews.map((item) => (
                  <div key={item._id}>
                    <span className="overview-date">
                      <b>{dayjs(item.start_date).tz(timezone).format("DD")}</b>
                      {dayjs(item.start_date).tz(timezone).format("MMM")}
                    </span>
                    <div>
                      <strong>
                        {item.role_title || statusName(item.status)}
                      </strong>
                      <small>{item.company_name || "Interview"}</small>
                      <em>
                        <ClockCircleOutlined />{" "}
                        {dayjs(item.start_date).tz(timezone).format("HH:mm")}
                      </em>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No upcoming interviews"
              />
            )}
          </article>
        </section>

        <section className="overview-bottom-grid">
          <article className="overview-panel overview-performance">
            <div className="overview-panel-head">
              <div>
                <h2>Application performance</h2>
                <p>
                  Applications, interviews, failed, and waiting over the last 7
                  days
                </p>
              </div>
              <Link to="/personal-statistic">Detailed statistic</Link>
            </div>
            <div className="overview-chart">
              {performance.map((day) => (
                <div className="overview-chart-day" key={day.label}>
                  <div>
                    <i
                      className="applications"
                      title={`${day.applications} applications`}
                      style={{
                        height: `${(day.applications / performanceMax) * 100}%`,
                      }}
                    />
                    <i
                      className="interviews"
                      title={`${day.interviews} interviews`}
                      style={{
                        height: `${(day.interviews / performanceMax) * 100}%`,
                      }}
                    />
                    <i
                      className="failed"
                      title={`${day.failed} failed`}
                      style={{
                        height: `${(day.failed / performanceMax) * 100}%`,
                      }}
                    />
                    <i
                      className="waiting"
                      title={`${day.waiting} waiting`}
                      style={{
                        height: `${(day.waiting / performanceMax) * 100}%`,
                      }}
                    />
                  </div>
                  <span>{day.label}</span>
                </div>
              ))}
            </div>
            <div className="overview-chart-legend">
              <span>
                <i /> Applications <b>{counts.total}</b>
              </span>
              <span>
                <i /> Interviews <b>{counts.interviews}</b>
              </span>
              <span>
                <i /> Failed <b>{counts.failed}</b>
              </span>
              <span>
                <i /> Waiting <b>{counts.waiting}</b>
              </span>
            </div>
          </article>
          <article className="overview-panel overview-breakdown">
            <div className="overview-panel-head">
              <div>
                <h2>Pipeline overview</h2>
                <p>Current application stages</p>
              </div>
            </div>
            <div
              className="overview-donut"
              style={{
                "--waiting-end": `${counts.total ? (counts.waiting / counts.total) * 100 : 0}%`,
                "--progress-end": `${counts.total ? ((counts.waiting + counts.progress) / counts.total) * 100 : 0}%`,
              }}
            >
              <div>
                <strong>{counts.total}</strong>
                <span>Total</span>
              </div>
            </div>
            <div className="overview-breakdown-list">
              <span>
                <i className="applied" />
                Waiting <b>{counts.waiting}</b>
              </span>
              <span>
                <i className="interview" />
                In progress <b>{counts.progress}</b>
              </span>
              <span>
                <i className="failed" />
                Failed stage <b>{counts.failed}</b>
              </span>
            </div>
          </article>
        </section>
      </Spin>
    </div>
  );
};

export default DashboardOverview;
